import { isString } from "radash";

import {
  isLocaleIso,
  isLocaleMorpheus,
  localeToIso,
  useI18nAddressInput,
} from "@solvari/translations";
import { isError } from "@solvari/utils";

import { alphabeticCompare } from "@/lib/helpers/strings";
import { onCreatedIfValue } from "@/lib/validation/events";
import { defineRule } from "@/lib/validation/rules/defineRule";

import {
  autocompleteNlNlAddress,
  getZipcodeCitySuggestions,
} from "./address.api.ts";
import { zipcodeRegex } from "./zipcodeRegex.ts";

const zipcodeRule = defineRule(
  (value: number | string | null | undefined, locale: string) => {
    if (!isLocaleIso(locale) && !isLocaleMorpheus(locale)) {
      return true;
    }
    const localeIso = localeToIso(locale);
    if (!value) {
      return false;
    }
    return zipcodeRegex[localeIso].test(value.toString());
  },
  (locale) => {
    const { tr } = useI18nAddressInput({ locale });
    return {
      name: "zipcode",
      message: () => tr("address_input.zipcode.validation.pattern"),
    };
  },
);

const zipcodeCityNlBeExistsRule = defineRule(
  async (zipcodeCity: unknown, locale: "da-DK" | "fr-BE" | "nl-BE") => {
    if (!zipcodeCity || typeof zipcodeCity !== "string") {
      return false;
    }
    const { zipcode, city } = splitZipcodeCity(zipcodeCity);

    const result = await getZipcodeCitySuggestions({
      city,
      locale,
      zipcode,
    });

    return (
      isError(result) ||
      result.some(
        (suggestion) =>
          suggestion.zipcode === zipcode ||
          alphabeticCompare(suggestion.city, city),
      )
    );
  },
  (locale) => {
    const { tr } = useI18nAddressInput({ locale });
    return {
      name: "exists",
      events: onCreatedIfValue(["blur"]),
      component: "manualToggle",
      color: "warning",
      message: () => tr("address_input.zipcode_city.validation.exists"),
    };
  },
);

function splitZipcodeCity(zipcodeCity: string) {
  const zipcodeCityWords = zipcodeCity.split(" ");

  const zipcode =
    zipcodeCityWords.find((zipcodeCityPart) =>
      /^\d+[a-z]*$/i.test(zipcodeCityPart),
    ) || "";
  const city = zipcodeCityWords
    .filter((zipcodeCityPart) => {
      return zipcodeCityPart !== "-" && /^\D+$/iu.test(zipcodeCityPart);
    })
    .join(" ");

  return { zipcode, city };
}

async function zipcodeNlBeExists(
  value: string,
  locale: "da-DK" | "fr-BE" | "nl-BE",
) {
  const result = await getZipcodeCitySuggestions({
    city: null,
    locale,
    zipcode: value,
  });

  return (
    isError(result) || result.some((suggestion) => suggestion.zipcode === value)
  );
}

async function zipcodeNlNlExists(value: string) {
  const autocompleteResult = await autocompleteNlNlAddress({
    zipcode: value.replace(" ", "").toUpperCase(),
  });
  return isError(autocompleteResult) || !!autocompleteResult;
}

const zipcodeExistsRule = defineRule(
  async (
    value: number | string | null | undefined,
    locale: "fr-BE" | "nl-BE" | "nl-NL",
  ) => {
    if (!isString(value) || !zipcodeRule(locale).validate(value)) {
      return false;
    }
    if (locale === "nl-NL") {
      return zipcodeNlNlExists(value);
    }
    return zipcodeNlBeExists(value, locale);
  },
  (locale) => {
    const { tr } = useI18nAddressInput({ locale });
    return {
      name: "exists",
      events: onCreatedIfValue(["blur"]),
      color: "warning",
      blocking: false,
      message: () => tr("address_input.zipcode.validation.exists"),
    };
  },
);

export {
  splitZipcodeCity,
  zipcodeCityNlBeExistsRule,
  zipcodeExistsRule,
  zipcodeRule,
};
